import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Input, Select } from '../../components/ui';
import api from '../../services/api';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

// ── MediaPicker component ─────────────────────────────────
function MediaPicker({ urls, onChange }) {
  const [tab, setTab]         = useState('library'); // 'library' | 'upload' | 'url'
  const [open, setOpen]       = useState(false);
  const [library, setLibrary] = useState([]);
  const [loadingLib, setLL]   = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const loadLibrary = async () => {
    setLL(true);
    try {
      const res = await fetch(`${MEDIA_URL}/api/media/files?type=images`);
      const data = await res.json();
      setLibrary(data.data || []);
    } catch { setLibrary([]); } finally { setLL(false); }
  };

  useEffect(() => { if (open && tab === 'library') loadLibrary(); }, [open, tab]);

  const addUrl = (url) => {
    if (!url || urls.includes(url)) return;
    onChange([...urls, url]);
  };

  const removeUrl = (url) => onChange(urls.filter(u => u !== url));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res  = await fetch(`${MEDIA_URL}/api/media/upload`, { method:'POST', body:fd });
      const data = await res.json();
      const newUrls = (data.data || []).map(f => f.url);
      onChange([...urls, ...newUrls]);
      setOpen(false);
    } catch { alert('Upload failed'); } finally { setUploading(false); }
  };

  return (
    <div>
      <label style={labelStyle}>Visuals / Media</label>

      {/* Attached images preview */}
      {urls.length > 0 && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
          {urls.map((url, i) => (
            <div key={i} style={{ position:'relative', width:80, height:80, borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
              <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                onError={e => { e.target.style.display='none'; }}/>
              <button onClick={() => removeUrl(url)}
                style={{ position:'absolute', top:3, right:3, width:18, height:18, borderRadius:'50%', background:'rgba(0,0,0,0.7)', border:'none', color:'#fff', fontSize:10, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>
                ✕
              </button>
            </div>
          ))}
          {urls.length < 4 && (
            <button onClick={() => setOpen(true)}
              style={{ width:80, height:80, borderRadius:8, border:'1.5px dashed var(--border)', background:'var(--bg-elevated)', color:'var(--text-muted)', fontSize:22, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              +
            </button>
          )}
        </div>
      )}

      {urls.length === 0 && (
        <button onClick={() => setOpen(true)}
          style={{ width:'100%', padding:'14px', borderRadius:8, border:'1.5px dashed var(--border)', background:'var(--bg-elevated)', color:'var(--text-muted)', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, transition:'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text-muted)'; }}>
          🖼 Add Images or Videos
        </button>
      )}

      <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
        Up to 4 images. Instagram requires at least 1 image.
      </div>

      {/* Media picker modal */}
      {open && (
        <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:700, background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:12, width:'100%', maxWidth:620, maxHeight:'80vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>

            {/* Header */}
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:14 }}>Add Media</span>
              <button onClick={() => setOpen(false)} style={{ color:'var(--text-muted)', fontSize:16, background:'none', border:'none', cursor:'pointer' }}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:'1px solid var(--border)', padding:'0 20px' }}>
              {[['library','📁 Media Library'],['upload','⬆ Upload'],['url','🔗 From URL']].map(([t,l]) => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ padding:'10px 16px', fontSize:12, fontWeight: tab===t?700:400, background:'none', border:'none', borderBottom:`2px solid ${tab===t?'var(--accent)':'transparent'}`, color: tab===t?'var(--accent)':'var(--text-muted)', cursor:'pointer', transition:'all 0.15s' }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div style={{ flex:1, overflow:'auto', padding:16 }}>

              {tab === 'library' && (
                loadingLib ? (
                  <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>Loading...</div>
                ) : library.length === 0 ? (
                  <div style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>
                    No images in media library yet. Upload some first.
                  </div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(100px, 1fr))', gap:8 }}>
                    {library.map(f => (
                      <div key={f.id} onClick={() => { addUrl(f.url); setOpen(false); }}
                        style={{ aspectRatio:'1', borderRadius:8, overflow:'hidden', border:`2px solid ${urls.includes(f.url)?'var(--accent)':'var(--border)'}`, cursor:'pointer', position:'relative', transition:'border-color 0.15s' }}>
                        <img src={f.url} alt={f.filename} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                        {urls.includes(f.url) && (
                          <div style={{ position:'absolute', inset:0, background:'rgba(232,164,58,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === 'upload' && (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:16, padding:20 }}>
                  <input ref={fileRef} type="file" multiple accept="image/*,video/*" style={{ display:'none' }} onChange={handleUpload}/>
                  <div onClick={() => fileRef.current.click()}
                    style={{ width:'100%', padding:40, border:'2px dashed var(--border)', borderRadius:12, textAlign:'center', cursor:'pointer', transition:'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor='var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
                    <div style={{ fontSize:32, marginBottom:8 }}>🖼</div>
                    <div style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)', marginBottom:4 }}>Click to upload</div>
                    <div style={{ fontSize:12, color:'var(--text-muted)' }}>JPG, PNG, GIF, MP4 — up to 50MB</div>
                  </div>
                  {uploading && <div style={{ color:'var(--accent)', fontSize:13 }}>Uploading...</div>}
                </div>
              )}

              {tab === 'url' && (
                <div style={{ display:'flex', flexDirection:'column', gap:12, padding:8 }}>
                  <p style={{ fontSize:13, color:'var(--text-muted)' }}>Paste a direct image or video URL:</p>
                  <input value={urlInput} onChange={e => setUrlInput(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'10px 12px', fontSize:13, boxSizing:'border-box' }}
                  />
                  {urlInput && (
                    <img src={urlInput} alt="Preview"
                      style={{ width:'100%', maxHeight:200, objectFit:'contain', borderRadius:8, border:'1px solid var(--border)' }}
                      onError={e => e.target.style.display='none'}/>
                  )}
                  <Button onClick={() => { addUrl(urlInput); setUrlInput(''); setOpen(false); }} disabled={!urlInput.trim()}>
                    Add This Image
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Platform config ───────────────────────────────────────
const PLATFORMS = [
  { id: 'twitter',   label: 'X / Twitter', icon: '𝕏', color: '#1DA1F2', limit: 280,  connected: false },
  { id: 'instagram', label: 'Instagram',   icon: '📸', color: '#E1306C', limit: 2200, connected: false },
  { id: 'linkedin',  label: 'LinkedIn',    icon: 'in', color: '#0A66C2', limit: 3000, connected: false },
  { id: 'tiktok',    label: 'TikTok',      icon: '🎵', color: '#ff0050', limit: 2200, connected: false },
  { id: 'whatsapp',  label: 'WhatsApp',    icon: '💬', color: '#25D366', limit: 4096, connected: true  },
];

const STATUS_COLORS = {
  draft:     { bg: 'rgba(107,114,128,0.15)', color: '#9ca3af' },
  scheduled: { bg: 'rgba(78,156,245,0.15)',  color: '#4e9cf5' },
  published: { bg: 'rgba(62,207,142,0.15)',  color: '#3ecf8e' },
  failed:    { bg: 'rgba(240,96,96,0.15)',   color: '#f06060' },
};

function PlatformBadge({ pid }) {
  const p = PLATFORMS.find(x => x.id === pid);
  if (!p) return null;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background:`${p.color}18`, color:p.color, border:`1px solid ${p.color}33` }}>
      <span style={{ fontSize:10 }}>{p.icon}</span>{p.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, background:s.bg, color:s.color, textTransform:'capitalize' }}>{status}</span>;
}

function CharCount({ text, limit }) {
  const remaining = limit - (text?.length || 0);
  const pct = (text?.length || 0) / limit;
  const color = pct > 0.9 ? '#f06060' : pct > 0.75 ? '#e8a43a' : 'var(--text-muted)';
  return <span style={{ fontSize:11, color }}>{remaining} chars left</span>;
}

// ── WhatsApp Broadcast Modal ──────────────────────────────
function WhatsAppModal({ onClose }) {
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all_enrolled');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await api.post('/admin/social/whatsapp-broadcast', { message, audience });
      setResult(res.data.data);
    } catch (err) {
      setResult({ error: err.response?.data?.error || 'Broadcast failed' });
    } finally { setSending(false); }
  };

  return (
    <Modal open onClose={onClose} title="WhatsApp Broadcast" width={520}>
      <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
        {result ? (
          <div style={{ textAlign:'center', padding:'20px 0' }}>
            {result.error ? (
              <><div style={{ fontSize:'2rem', marginBottom:12 }}>⚠️</div>
              <div style={{ color:'var(--red-light)', marginBottom:8 }}>{result.error}</div></>
            ) : (
              <><div style={{ fontSize:'2rem', marginBottom:12 }}>✓</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:18, color:'#25D366', marginBottom:8 }}>Broadcast Sent!</div>
              <div style={{ fontSize:13, color:'var(--text-muted)' }}>Sent to {result.sent || 0} recipients</div></>
            )}
            <Button onClick={onClose} style={{ margin:'16px auto 0' }}>Close</Button>
          </div>
        ) : (
          <>
            <div style={{ padding:'12px 16px', background:'rgba(37,211,102,0.08)', border:'1px solid rgba(37,211,102,0.25)', borderRadius:8, fontSize:12, color:'#25D366' }}>
              💬 Messages will be sent via WhatsApp Business API to opted-in contacts only.
            </div>
            <Select label="Audience" value={audience} onChange={e => setAudience(e.target.value)}>
              <option value="all_enrolled">All Enrolled Students</option>
              <option value="active_cohorts">Active Cohort Members</option>
              <option value="partners">Partner Institutions</option>
              <option value="trainers">Trainers</option>
              <option value="all">All Contacts (opted-in)</option>
            </Select>
            <div>
              <label style={labelStyle}>Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
                placeholder="Type your WhatsApp message here..."
                style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'10px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
                <span style={{ fontSize:11, color:'var(--text-muted)' }}>Supports basic text and emojis</span>
                <CharCount text={message} limit={4096}/>
              </div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button onClick={send} loading={sending} disabled={!message.trim()}
                style={{ background:'#25D366', color:'#fff', border:'none' }}>
                Send Broadcast
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Compose Modal ─────────────────────────────────────────
function ComposeModal({ post, onClose, onSaved }) {
  const [form, setForm] = useState({
    content:    post?.content    || '',
    platforms:  post?.platforms  || [],
    status:     post?.status     || 'draft',
    scheduled_at: post?.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0,16) : '',
    media_urls: post?.media_urls || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const togglePlatform = (pid) => {
    set('platforms', form.platforms.includes(pid)
      ? form.platforms.filter(p => p !== pid)
      : [...form.platforms, pid]
    );
  };

  const save = async () => {
    if (!form.content.trim() || !form.platforms.length) return;
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at || null };
      if (post?.id) await api.put(`/admin/social/posts/${post.id}`, payload);
      else          await api.post('/admin/social/posts', payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const publish = async () => {
    if (!form.platforms.length) return;
    setSaving(true);
    try {
      if (post?.id) {
        await api.put(`/admin/social/posts/${post.id}`, { ...form, status:'published' });
        await api.post(`/admin/social/posts/${post.id}/publish`);
      } else {
        const res = await api.post('/admin/social/posts', { ...form, status:'published' });
        await api.post(`/admin/social/posts/${res.data.data.id}/publish`);
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Publish failed');
    } finally { setSaving(false); }
  };

  const shortestLimit = form.platforms.length
    ? Math.min(...form.platforms.map(pid => PLATFORMS.find(p => p.id === pid)?.limit || 99999))
    : 2200;

  return (
    <Modal open onClose={onClose} title={post ? 'Edit Post' : 'Compose Post'} width={680}>
      <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

        {/* Platform selector */}
        <div>
          <label style={labelStyle}>Publish to</label>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {PLATFORMS.map(p => (
              <button key={p.id} onClick={() => togglePlatform(p.id)}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, transition:'all 0.15s',
                  background: form.platforms.includes(p.id) ? `${p.color}20` : 'var(--bg-elevated)',
                  border: `1.5px solid ${form.platforms.includes(p.id) ? p.color : 'var(--border)'}`,
                  color: form.platforms.includes(p.id) ? p.color : 'var(--text-muted)',
                  opacity: p.connected ? 1 : 0.5,
                }}>
                <span>{p.icon}</span>{p.label}
                {!p.connected && <span style={{ fontSize:9, opacity:0.7 }}>(not connected)</span>}
              </button>
            ))}
          </div>
          {form.platforms.some(pid => !PLATFORMS.find(p => p.id === pid)?.connected) && (
            <div style={{ marginTop:8, fontSize:11, color:'var(--text-muted)', padding:'8px 12px', background:'var(--bg-elevated)', borderRadius:6 }}>
              ⚠️ Some selected platforms are not connected yet. Posts will be saved as drafts for those platforms.
            </div>
          )}
        </div>

        {/* Content */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <label style={{ ...labelStyle, marginBottom:0 }}>Content</label>
            <CharCount text={form.content} limit={shortestLimit}/>
          </div>
          <textarea value={form.content} onChange={e => set('content', e.target.value)} rows={6}
            placeholder="What's on your mind? Share updates, achievements, announcements..."
            style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'10px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box', lineHeight:1.6 }}
          />
          {/* Quick tags */}
          <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
            {['#MilleHills', '#Rwanda', '#TechAfrica', '#Innovation', '#Gisenyi'].map(tag => (
              <button key={tag} onClick={() => set('content', form.content + (form.content ? ' ' : '') + tag)}
                style={{ fontSize:11, padding:'3px 8px', borderRadius:20, cursor:'pointer', background:'var(--bg-elevated)', border:'1px solid var(--border)', color:'var(--text-muted)', transition:'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text-muted)'; }}
              >{tag}</button>
            ))}
          </div>
        </div>

        {/* Preview per platform */}
        {form.platforms.length > 0 && form.content && (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <label style={labelStyle}>Preview</label>
            {form.platforms.map(pid => {
              const p = PLATFORMS.find(x => x.id === pid);
              const truncated = form.content.length > p.limit ? form.content.slice(0, p.limit - 3) + '...' : form.content;
              return (
                <div key={pid} style={{ padding:'12px 14px', background:'var(--bg-elevated)', border:`1px solid ${p.color}33`, borderRadius:8 }}>
                  <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:8 }}>
                    <span style={{ fontSize:14 }}>{p.icon}</span>
                    <span style={{ fontSize:11, fontWeight:600, color:p.color }}>{p.label}</span>
                    {form.content.length > p.limit && <span style={{ fontSize:10, color:'#f06060' }}>⚠ Will be truncated</span>}
                  </div>
                  <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{truncated}</div>
                  {form.media_urls?.length > 0 && (
                    <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                      {form.media_urls.slice(0, pid==='twitter'?4:pid==='instagram'?10:4).map((url,i) => (
                        <img key={i} src={url} alt="" style={{ width:60, height:60, objectFit:'cover', borderRadius:6, border:'1px solid var(--border)' }}
                          onError={e => e.target.style.display='none'}/>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Visuals / Media */}
        <MediaPicker
          urls={form.media_urls}
          onChange={urls => set('media_urls', urls)}
        />

        {/* Schedule */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div>
            <label style={labelStyle}>Schedule For (optional)</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={e => set('scheduled_at', e.target.value)}
              min={new Date().toISOString().slice(0,16)}
              style={{ width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'8px 12px', fontSize:13, boxSizing:'border-box' }}
            />
          </div>
          <Select label="Status" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
          </Select>
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', paddingTop:4 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" onClick={save} loading={saving} disabled={!form.content.trim() || !form.platforms.length}>
            Save Draft
          </Button>
          <Button onClick={publish} loading={saving} disabled={!form.content.trim() || !form.platforms.length}>
            {form.scheduled_at ? '📅 Schedule' : '🚀 Publish Now'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Connect Platform Modal ────────────────────────────────
function ConnectModal({ platform, onClose }) {
  const instructions = {
    twitter:   { steps: ['Go to developer.twitter.com', 'Create a new App', 'Generate Bearer Token + API keys', 'Add to your backend .env as TWITTER_API_KEY, TWITTER_API_SECRET, TWITTER_ACCESS_TOKEN, TWITTER_ACCESS_SECRET'] },
    instagram: { steps: ['Set up a Facebook Business account', 'Connect your Instagram Professional account', 'Create a Meta App at developers.facebook.com', 'Add INSTAGRAM_ACCESS_TOKEN + INSTAGRAM_BUSINESS_ID to .env'] },
    linkedin:  { steps: ['Go to linkedin.com/developers', 'Create an app with "Share on LinkedIn" permission', 'Get authorization code via OAuth 2.0', 'Add LINKEDIN_ACCESS_TOKEN to .env'] },
    tiktok:    { steps: ['Apply at developers.tiktok.com', 'Create app with Content Posting API access', 'Complete OAuth flow', 'Add TIKTOK_ACCESS_TOKEN to .env'] },
  };
  const info = instructions[platform?.id] || { steps: [] };

  return (
    <Modal open onClose={onClose} title={`Connect ${platform?.label}`} width={480}>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <div style={{ padding:'14px 16px', background:`${platform?.color}12`, border:`1px solid ${platform?.color}33`, borderRadius:8 }}>
          <div style={{ fontSize:13, fontWeight:600, color:platform?.color, marginBottom:4 }}>{platform?.icon} {platform?.label}</div>
          <div style={{ fontSize:12, color:'var(--text-muted)' }}>Follow these steps to connect your account:</div>
        </div>
        <ol style={{ paddingLeft:20, display:'flex', flexDirection:'column', gap:10 }}>
          {info.steps.map((step, i) => (
            <li key={i} style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>{step}</li>
          ))}
        </ol>
        <div style={{ padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8, fontFamily:'monospace', fontSize:12, color:'var(--text-muted)' }}>
          After adding to .env, restart the backend and refresh this page.
        </div>
        <Button onClick={onClose} style={{ alignSelf:'flex-end' }}>Got it</Button>
      </div>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────
export default function SocialPage() {
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(null); // 'compose' | 'whatsapp' | 'connect'
  const [selected, setSelected]     = useState(null);
  const [connectPlatform, setCP]    = useState(null);
  const [filterStatus, setFilter]   = useState('');
  const [stats, setStats]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, statsRes] = await Promise.allSettled([
        api.get('/admin/social/posts', { params: filterStatus ? { status: filterStatus } : {} }),
        api.get('/admin/social/stats'),
      ]);
      if (postsRes.status === 'fulfilled') setPosts(postsRes.value.data.data || []);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data.data);
    } catch { setPosts([]); } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/admin/social/posts/${id}`);
    load();
  };

  const publishNow = async (id) => {
    try {
      await api.post(`/admin/social/posts/${id}/publish`);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Publish failed'); }
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, marginBottom:2 }}>Social Media</div>
          <div style={{ fontSize:12, color:'var(--text-muted)' }}>Compose, schedule and publish across platforms</div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <Button variant="secondary" onClick={() => setModal('whatsapp')}
            style={{ background:'rgba(37,211,102,0.1)', borderColor:'rgba(37,211,102,0.3)', color:'#25D366' }}>
            💬 WhatsApp Broadcast
          </Button>
          <Button onClick={() => { setSelected(null); setModal('compose'); }}>
            + Compose Post
          </Button>
        </div>
      </div>

      {/* Platform status cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px,1fr))', gap:12 }}>
        {PLATFORMS.map(p => (
          <div key={p.id} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px 16px', display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:20 }}>{p.icon}</span>
              <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
                background: p.connected ? 'rgba(62,207,142,0.12)' : 'rgba(107,114,128,0.12)',
                color: p.connected ? '#3ecf8e' : '#6b7280',
                border: `1px solid ${p.connected ? 'rgba(62,207,142,0.3)' : 'rgba(107,114,128,0.2)'}`,
              }}>{p.connected ? '● Connected' : '○ Not connected'}</span>
            </div>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-primary)' }}>{p.label}</div>
            {!p.connected ? (
              <button onClick={() => { setCP(p); setModal('connect'); }}
                style={{ fontSize:11, padding:'5px 0', borderRadius:6, cursor:'pointer', background:`${p.color}15`, border:`1px solid ${p.color}33`, color:p.color, fontWeight:600, transition:'all 0.15s' }}>
                Connect →
              </button>
            ) : (
              <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                {stats?.[p.id]?.followers ? `${Number(stats[p.id].followers).toLocaleString()} followers` : 'Ready to post'}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
          {[
            { label:'Total Posts',    value: stats.total     || 0, color:'var(--accent)' },
            { label:'Published',      value: stats.published || 0, color:'#3ecf8e' },
            { label:'Scheduled',      value: stats.scheduled || 0, color:'#4e9cf5' },
            { label:'Drafts',         value: stats.draft     || 0, color:'var(--text-muted)' },
          ].map(s => (
            <div key={s.label} style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px 18px' }}>
              <div style={{ fontSize:22, fontWeight:700, color:s.color, fontFamily:'var(--font-display)' }}>{s.value}</div>
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter + Posts list */}
      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
          <span style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:13 }}>Posts</span>
          <div style={{ display:'flex', gap:6 }}>
            {['','draft','scheduled','published','failed'].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                style={{ padding:'5px 12px', borderRadius:20, fontSize:11, cursor:'pointer', fontWeight: filterStatus===s ? 700 : 400,
                  background: filterStatus===s ? 'var(--accent-muted)' : 'var(--bg-elevated)',
                  border: `1px solid ${filterStatus===s ? 'var(--accent)' : 'var(--border)'}`,
                  color: filterStatus===s ? 'var(--accent)' : 'var(--text-muted)',
                  transition:'all 0.15s', textTransform:'capitalize',
                }}>{s || 'All'}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ padding:60, textAlign:'center', color:'var(--text-muted)' }}>Loading...</div>
        ) : posts.length === 0 ? (
          <div style={{ padding:60, textAlign:'center' }}>
            <div style={{ fontSize:'2rem', marginBottom:12, opacity:0.3 }}>◎</div>
            <div style={{ color:'var(--text-muted)', marginBottom:16 }}>No posts yet</div>
            <Button onClick={() => { setSelected(null); setModal('compose'); }} style={{ margin:'0 auto' }}>
              Compose your first post
            </Button>
          </div>
        ) : (
          <div>
            {posts.map(post => (
              <div key={post.id} style={{ padding:'16px 20px', borderBottom:'1px solid var(--border-subtle)', display:'flex', gap:16, alignItems:'flex-start' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
                    <StatusBadge status={post.status}/>
                    {(post.platforms || []).map(pid => <PlatformBadge key={pid} pid={pid}/>)}
                    {post.scheduled_at && (
                      <span style={{ fontSize:11, color:'var(--text-muted)' }}>
                        📅 {new Date(post.scheduled_at).toLocaleString('en-RW', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>
                    {post.content?.slice(0,200)}{post.content?.length > 200 ? '…' : ''}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
                    {new Date(post.created_at).toLocaleDateString('en-RW', { month:'short', day:'numeric', year:'numeric' })}
                    {post.published_at && ` · Published ${new Date(post.published_at).toLocaleString('en-RW', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}`}
                  </div>
                  {post.error_message && (
                    <div style={{ marginTop:6, fontSize:11, color:'#f06060' }}>⚠ {post.error_message}</div>
                  )}
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  {post.status === 'draft' && (
                    <Button size="sm" variant="ghost" onClick={() => publishNow(post.id)}>Publish</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setSelected(post); setModal('compose'); }}>Edit</Button>
                  <Button size="sm" variant="ghost" style={{ color:'var(--red-light)' }} onClick={() => deletePost(post.id)}>Del</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === 'compose' && (
        <ComposeModal post={selected} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>
      )}
      {modal === 'whatsapp' && <WhatsAppModal onClose={() => { setModal(null); load(); }}/>}
      {modal === 'connect' && <ConnectModal platform={connectPlatform} onClose={() => setModal(null)}/>}
    </div>
  );
}

const labelStyle = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };